import type { PixelFrame } from "pixelfx";

/**
 * Аура Ярости при полной шкале (консультант 04.11.2026: «заметно с первого взгляда, бойцы при этом читаются»):
 * тёплое свечение по контуру с пульсом раз в 0,5 с, языки огня вверх от плеч и рук, искры кружат вокруг тела.
 * Только свет (буфер light): краска бойца не меняется — контраст держится. Всё — от кадра мира, без своего времени.
 */
const PULSE_FRAMES = 30;
const HALO = 5;
const FLAMES = 64;
const FLAME_LIFE = 30;
const SPARKS = 16;
/** Ниже такой высоты тела (пиксели арта) — лёжа или в подкате — ауру не рисуем. */
const LOW_BODY = 90;

/** Прибавить тёплый свет в точку кадра (ABGR, насыщение 255). */
function addLight(frame: PixelFrame, x: number, y: number, r: number, g: number, b: number): void {
  if (x < 0 || y < 0 || x >= frame.w || y >= frame.h) return;
  const index = y * frame.w + x;
  const old = frame.light[index] ?? 0;
  const nr = Math.min(255, (old & 255) + r);
  const ng = Math.min(255, ((old >>> 8) & 255) + g);
  const nb = Math.min(255, ((old >>> 16) & 255) + b);
  frame.light[index] = ((255 << 24) | (nb << 16) | (ng << 8) | nr) >>> 0;
}

/** Целый хеш для случайного, но повторимого выбора (огонь одинаков при откате и в записи). */
const hash = (value: number): number => {
  let h = Math.imul(value ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
};

/**
 * Нарисовать ауру бойца side (маска = side + 1). cx, feet — середина и стопы бойца в пикселях экрана арта.
 */
export function drawRageAura(
  frame: PixelFrame,
  mask: Uint8Array,
  side: number,
  cx: number,
  feet: number,
  tick: number,
): void {
  const id = side + 1;
  const left = Math.max(0, cx - 110);
  const right = Math.min(frame.w - 1, cx + 110);
  const top = Math.max(0, feet - 230);
  const bottom = Math.min(frame.h - 1, feet + 4);
  const pulse = 0.55 + 0.45 * Math.sin((tick / PULSE_FRAMES) * Math.PI * 2);
  const inside = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < frame.w && y < frame.h && mask[y * frame.w + x] === id;

  // лёжа и в подкате (тело ниже LOW_BODY) ауры нет: низкая поза у пола не тонет в собственном свете
  let highest = bottom;
  for (let y = top; y <= bottom && highest === bottom; y++)
    for (let x = left; x <= right; x++)
      if (inside(x, y)) {
        highest = y;
        break;
      }
  if (feet - highest < LOW_BODY) return;

  // 1. свечение по контуру: пиксели снаружи тела в пределах HALO — тем ярче, чем ближе к телу
  const tops: number[] = [];
  for (let y = top; y <= bottom; y++) {
    for (let x = left; x <= right; x++) {
      if (inside(x, y)) continue;
      let near = 0;
      for (let d = 1; d <= HALO && near === 0; d++)
        if (inside(x + d, y) || inside(x - d, y) || inside(x, y + d) || inside(x, y - d)) near = d;
      if (near === 0) continue;
      // у пола свечение тише: низкие позы (подкат, кувырок) не тонут в собственном ореоле — контраст 3:1
      const height = Math.min(1, Math.max(0, (feet - y - 30) / 90));
      if (height === 0) continue;
      const k = ((HALO + 1 - near) / HALO) * pulse * height;
      addLight(frame, x, y, Math.round(210 * k), Math.round(88 * k), Math.round(18 * k));
      // верхний край тела — отсюда растут языки огня
      if (near === 1 && inside(x, y + 1) && y < feet - 60) tops.push(y * frame.w + x);
    }
  }

  // 2. языки огня: от верхних краёв (плечи, руки, голова) вверх, качаются и гаснут
  if (tops.length > 0) {
    for (let flame = 0; flame < FLAMES; flame++) {
      const age = (tick + flame * 7) % FLAME_LIFE;
      const birth = tick - age;
      const seed = hash(birth * 131 + flame * 977 + side * 31);
      const base = tops[seed % tops.length] ?? 0;
      const bx = base % frame.w;
      const by = Math.floor(base / frame.w);
      const u = age / FLAME_LIFE;
      const x = bx + Math.round(Math.sin((age + flame) * 0.5) * (1 + u * 3));
      const y = by - Math.round(age * 2.1);
      const k = (1 - u) * (0.7 + 0.3 * pulse);
      // жёлтое ядро → оранжевый → красный хвост
      // язык — капля 2×3 с ядром, к концу жизни тоньше
      const width = u < 0.6 ? 2 : 1;
      for (let dy = 0; dy < 3; dy++)
        for (let dx = 0; dx < width; dx++) {
          const fade = dy === 0 ? 1 : 0.7;
          addLight(
            frame,
            x + dx,
            y - dy,
            Math.round(240 * k * fade),
            Math.round((160 - 100 * u) * k * fade),
            Math.round(36 * (1 - u) * k * fade),
          );
        }
    }
  }

  // 3. искры по кругу вокруг тела: эллипс, вращение, у дальней стороны — тусклее
  const middle = feet - 100;
  for (let spark = 0; spark < SPARKS; spark++) {
    const angle = tick * 0.09 + (spark * Math.PI * 2) / SPARKS;
    const near = Math.sin(angle) > 0;
    const x = cx + Math.round(Math.cos(angle) * 66);
    const y = middle + Math.round(Math.sin(angle) * 22 - ((spark % 3) - 1) * 40);
    if (!near && inside(x, y)) continue; // за телом — спрятана
    const k = near ? 1 : 0.55;
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const core = dx === 0 && dy === 0 ? 1 : 0.6;
        addLight(
          frame,
          x + dx,
          y + dy,
          Math.round(255 * k * core),
          Math.round(160 * k * core),
          Math.round(50 * k * core),
        );
      }
  }
}
